import * as React from "react";
import classNames from "classnames";
import { isSameDay } from "date-fns";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { connect } from "react-redux";

import Typography from "@material-ui/core/Typography";
import { Reservation } from "../../api/reservations";
import { Table } from "../../api/tables";
import { StoreState } from "../../reducers/root-reducer";
import { ConnectedListSection } from "../inquiry-list/list-section";
import {
	getReservationsForTable,
	getSelectedOccupiedDay,
	getTotalSoftLimitForTable,
	getPeopleCountSumForTable
} from "../../util/general-utility";
import { ConnectedPlannerListReservation } from "./reservation";
import { OccupiedDay } from "../../api/occupied-days";
import { getGroupDataForTable } from "../../util/table-link-utility";
import { setMovingReservation } from "../../actions/store/planner-list-actions";
import { Dispatch } from "../../../node_modules/redux";
import { updateSingleReservation } from "../../actions/reservation-actions";
import {
	removeTableFromLinkGroup,
	addTableToLinkGroup
} from "../../actions/store/app-actions";

interface State {}

export interface UnsortedReservationSectionProps {
	readOnly?: Boolean;
}

interface ConnectedUnsortedReservationSectionProps
	extends WithStyles<typeof styles> {
	reservations: Reservation[];
	selectedDate: Date;
	movingReservationId: string;
	setMovingReservation: (id: string) => void;
}

type Props = UnsortedReservationSectionProps &
	ConnectedUnsortedReservationSectionProps;

export class UnsortedReservationSection extends React.Component<Props, {}> {
	private handleTitleClick = () => {
		const {
			reservations,
			setMovingReservation,
			movingReservationId,
			readOnly
		} = this.props;

		if (readOnly) return;

		const movingReservation = reservations.find(
			res => res.id === movingReservationId
		);

		if (movingReservation) {
			updateSingleReservation(movingReservation.id, {
				...movingReservation,
				tableId: null
			});
		}

		setMovingReservation(null);
	};

	private renderTableContent = () => {
		const { reservations, selectedDate, classes, readOnly } = this.props;

		const unsortedReservations = reservations.filter(
			r => !r.tableId && isSameDay(r.date, selectedDate)
		);

		if (unsortedReservations.length === 0) {
			return (
				<div className={classes.noReservationAvailable}>
					<Typography className={classes.noReservationAvailableText}>
						Alle Reservierungen eingeteilt!
					</Typography>
				</div>
			);
		}

		return unsortedReservations.map(res => (
			<ConnectedPlannerListReservation
				key={res.id}
				readOnly={readOnly}
				{...res}
			/>
		));
	};

	private renderSectionOverlay = () => {
		const { classes, movingReservationId, readOnly } = this.props;

		if (readOnly) return null;

		return (
			<div
				className={classNames(classes.sectionOverlay, {
					[classes.sectionOverlayVisible]: Boolean(
						movingReservationId
					)
				})}
			>
				<Typography className={classes.sectionOverlayText}>
					Zum Verschieben hier tippen
				</Typography>
				<svg
					className={classes.sectionOverlayBorder}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
				>
					<rect
						x="0"
						y="0"
						width="100"
						height="100"
						className={classes.sectionOverlayPath}
						vector-effect="non-scaling-stroke"
					/>
				</svg>
			</div>
		);
	};

	public render() {
		return (
			<ConnectedListSection
				title="Noch nicht eingeteilt"
				onTitleClick={this.handleTitleClick}
				overlay={this.renderSectionOverlay()}
			>
				{this.renderTableContent()}
			</ConnectedListSection>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables,
	reservations: state.reservations,
	selectedDate: state.date,
	movingReservationId: state.plannerListMovingReservation
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setMovingReservation: (id: string) => dispatch(setMovingReservation(id))
});

const styles = (theme: Theme) =>
	createStyles({
		noReservationAvailable: {
			padding: "20px 0"
		},
		noReservationAvailableText: {
			fontStyle: "italic",
			color: "#999",

			"@media (max-width: 959px)": {
				paddingLeft: 10
			}
		},
		sectionOverlay: {
			position: "absolute",
			top: "0",
			left: "0",
			width: "100%",
			height: "100%",
			background: "rgba(0, 0, 0, 0.3)",
			padding: 10,
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			opacity: 0,
			pointerEvents: "none",
			transition: "opacity 0.2s ease"
		},
		sectionOverlayVisible: {
			opacity: 1
		},
		sectionOverlayBorder: {
			position: "absolute",
			top: 10,
			left: 10,
			right: 10,
			bottom: 10,
			width: "calc(100% - 20px)",
			height: "calc(100% - 20px)"
		},
		sectionOverlayPath: {
			fill: "none",
			stroke: "white",
			strokeWidth: 3,
			strokeDasharray: "10, 10"
		},
		sectionOverlayText: {
			color: "white",
			fontWeight: 600
		}
	});

export const ConnectedUnsortedReservationSection = withStyles(styles)(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(UnsortedReservationSection)
);
